import { useId, useState } from 'react';
import type { PointComparable } from '../../lib/training.ts';
import { ancrage, colonne, rayon } from './cadre.ts';

/* La progression, une fois le programme neutralisé.
 *
 * ── Ce que la courbe des taux ne pouvait pas dire ───────────────────────────
 *
 * Le taux d'une journée de practice mesure surtout ce qu'on a CHOISI de
 * travailler ce jour-là. Un atelier volontairement dur, un club ingrat, et la
 * courbe plonge sans que rien n'ait bougé dans le geste. Sur un historique
 * réel, l'écart entre l'atelier le plus dur et le plus facile atteint
 * vingt-six points, celui entre le club le plus sûr et le moins sûr dix-sept :
 * de quoi expliquer toute l'amplitude d'une courbe sans qu'il s'y soit rien
 * passé.
 *
 * Ici, chaque atelier n'est comparé qu'à LUI-MÊME, joué avec le même club. La
 * difficulté s'annule dans la soustraction, et ce qui reste se lit en points de
 * taux : « +6 », c'est six points de mieux que son ordinaire, à programme
 * équivalent.
 *
 * ── Pourquoi zéro est une LIGNE et non un plancher ──────────────────────────
 *
 * L'axe est symétrique et zéro est au milieu : la mesure est un écart, et un
 * écart a deux sens. Poser zéro en bas ferait d'une journée ordinaire un échec
 * visuel. La ligne de zéro porte donc son nom — « mon ordinaire » — parce qu'un
 * axe dont le point de référence n'est pas écrit se lit comme un objectif.
 *
 * ── Pourquoi certaines marques sont pleines et d'autres creuses ─────────────
 *
 * Une journée n'a rien prouvé tant que son écart tient dans sa marge : trente
 * balles suffisent à produire dix points d'écart par pur tirage. Les marques
 * PLEINES sont les journées qui sortent de leur marge, les CREUSES celles qui
 * n'y sortent pas. C'est une différence de forme et non de couleur : elle se
 * lit au soleil, elle se lit en noir et blanc, et elle se lit pour qui ne
 * distingue pas le rouge du vert.
 */

const W = 560;
const H = 190;
const PAD_T = 14;
const PAD_B = 22;
const PAD_R = 46;
const PLOT_H = H - PAD_T - PAD_B;

/** `YYYY-MM-DD` → « 13 août », sans passer par `Date` sur une chaîne nue —
 *  la spécification l'interprète en UTC, et l'ouest de Greenwich verrait la
 *  veille. */
const shortDate = (day: string): string => {
    const [y, m, d] = day.split('-').map(Number);
    return new Date(y!, m! - 1, d!).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const signe = (v: number): string =>
    `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toLocaleString('fr-FR', {
        minimumFractionDigits: 1, maximumFractionDigits: 1,
    })}`;

export function CourbeComparable({ points }: { points: PointComparable[] }) {
    const titleId = useId();
    const [table, setTable] = useState(false);
    const [hover, setHover] = useState<number | null>(null);

    if (points.length < 2) return null;

    const plotW = W - PAD_R;
    const x = (i: number) => (i / (points.length - 1)) * plotW;

    /* L'échelle englobe les BANDES et pas seulement les points : une marge
     * rognée au bord donnerait à croire qu'on en sait plus qu'on n'en sait.
     * Arrondie au multiple de cinq supérieur, avec un plancher à dix — sous
     * quoi les oscillations d'une bonne série se liraient comme des falaises. */
    const brut = Math.max(10, ...points.map((p) => Math.abs(p.ecart) + p.marge));
    const MAX = Math.ceil(brut / 5) * 5;
    const y = (v: number) => PAD_T + PLOT_H / 2 - (Math.max(-MAX, Math.min(MAX, v)) / MAX) * (PLOT_H / 2);

    const ligne = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(p.ecart)}`).join(' ');
    const bande = [
        ...points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(p.ecart + p.marge)}`),
        ...[...points].reverse().map((p, i) => `L ${x(points.length - 1 - i)} ${y(p.ecart - p.marge)}`),
        'Z',
    ].join(' ');

    const last = points[points.length - 1]!;
    const sorti = (p: PointComparable) => Math.abs(p.ecart) > p.marge;
    // Les bornes de taille se prennent sur la série : c'est un ordre de
    // grandeur RELATIF aux autres journées, pas une mesure absolue.
    const balles = points.map((p) => p.balls);
    const mini = Math.min(...balles);
    const maxi = Math.max(...balles);
    const graduations = [MAX, MAX / 2, 0, -MAX / 2, -MAX];

    return (
        <div>
            <p className="mb-2 text-xs opacity-60">
                Ici la difficulté ne compte plus : chaque atelier est comparé à
                lui-même, avec le même club.
            </p>

            <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-labelledby={titleId}>
                <title id={titleId}>
                    Écart à votre ordinaire, en points de taux, du {shortDate(points[0]!.day)}
                    {' au '}{shortDate(last.day)}
                </title>

                {graduations.map((v) => (
                    <g key={v}>
                        <line x1={0} x2={plotW} y1={y(v)} y2={y(v)}
                            stroke={v === 0 ? 'var(--edge)' : 'var(--chart-grid)'}
                            strokeWidth={v === 0 ? 1.5 : 1} />
                        <text x={plotW + 6} y={y(v)} dominantBaseline="central"
                            className={`fill-base-content text-[11px] ${v === 0 ? 'opacity-70' : 'opacity-40'}`}>
                            {v === 0 ? '0' : signe(v).replace(',0', '')}
                        </text>
                    </g>
                ))}

                {/* La bande d'incertitude, sous la ligne : même teinte, très
                    pâle. C'est la MÊME mesure, pas une seconde série. */}
                <path d={bande} fill="var(--chart-current)" fillOpacity={0.14} stroke="none" />

                {/* Zéro porte son NOM, et pas seulement son chiffre : sur un axe
                    symétrique, une ligne de référence anonyme se lit comme un
                    objectif à atteindre, alors qu'elle dit « là où vous en êtes
                    d'habitude ».

                    Écrite APRÈS la bande : peinte avant, l'aplat passait par
                    dessus et la délavait — illisible au soleil, c'est-à-dire
                    là où on lit cet écran. */}
                <text x={2} y={y(0) + 13}
                    className="fill-base-content text-[10px] uppercase tracking-wide opacity-60">
                    mon ordinaire
                </text>

                <path d={ligne} fill="none" stroke="var(--chart-current)"
                    strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />

                {/* LE REPÈRE DE SURVOL : un filet vertical sur la colonne
                    pointée.
                    Ni la taille ni le remplissage ne peuvent plus servir à dire
                    « c'est celle-ci » — la première porte le nombre de balles,
                    le second le verdict. Le filet, lui, ne code rien d'autre
                    que la position du doigt, et il se voit même quand la marque
                    est minuscule. C'est aussi ce que fait n'importe quelle
                    courbe qu'on interroge : on suit une verticale. */}
                {hover !== null && (
                    <line x1={x(hover)} x2={x(hover)} y1={PAD_T} y2={PAD_T + PLOT_H}
                        stroke="var(--club)" strokeWidth={1} strokeOpacity={0.45} />
                )}

                {points.map((p, i) => (
                    <g key={p.day}
                        onMouseEnter={() => setHover(i)}
                        onMouseLeave={() => setHover(null)}>
                        {/* Cible de survol : toute la colonne. Viser un disque
                            de dix pixels au doigt ganté est impossible. Bornes
                            à mi-chemin des voisins, et jusqu'au bord pour les
                            deux du bout. */}
                        <rect {...colonne(i, points.length, x, W)} y={0}
                            height={H} fill="transparent" />
                        {/* Trois choses sur une même marque, et trois choses
                            différentes : le REMPLISSAGE dit si l'écart compte,
                            la TAILLE dit sur combien de balles il repose, et la
                            teinte dit lequel on survole.
                            L'une répond « peut-on y croire », l'autre « d'où
                            ça sort ».

                            Au survol, la marque change de TEINTE et jamais de
                            taille : la grossir lui prêterait des balles qu'elle
                            n'a pas. Une journée creuse reste creuse — la
                            remplir la ferait passer pour concluante ; c'est le
                            filet vertical qui dit où l'on pointé. */}
                        <circle cx={x(i)} cy={y(p.ecart)}
                            r={rayon(p.balls, mini, maxi)}
                            fill={sorti(p)
                                ? (hover === i ? 'var(--club)' : 'var(--chart-current)')
                                : 'var(--color-base-100)'}
                            stroke={hover === i ? 'var(--club)' : 'var(--chart-current)'}
                            strokeWidth={2} />
                    </g>
                ))}

                <text x={x(points.length - 1)} y={y(last.ecart) - 12} textAnchor="end"
                    className="fill-base-content text-[13px] font-bold tabular-nums">
                    {signe(last.ecart)}
                </text>

                {/* Les deux dates s'effacent pendant le survol : l'étiquette
                    qui les remplace dit déjà le jour. */}
                {hover === null && (
                    <>
                        <text x={0} y={H - 6} className="fill-base-content text-[11px] opacity-50">
                            {shortDate(points[0]!.day)}
                        </text>
                        <text x={plotW} y={H - 6} textAnchor="end"
                            className="fill-base-content text-[11px] opacity-50">
                            {shortDate(last.day)}
                        </text>
                    </>
                )}

                {hover !== null && (
                    <text x={x(hover)} y={H - 6} textAnchor={ancrage(x(hover), plotW)}
                        className="fill-base-content text-[11px] font-semibold">
                        {shortDate(points[hover]!.day)} · {signe(points[hover]!.ecart)}
                        {' ± '}{points[hover]!.marge.toFixed(0)}
                        {' · '}{points[hover]!.balls} balles
                    </text>
                )}
            </svg>

            <p className="mt-1 px-0.5 text-xs opacity-50">
                Point plein : l’écart compte. Point creux : trop peu de balles pour
                conclure. La taille dit combien de balles.
            </p>

            <button type="button" className="btn btn-ghost btn-xs mt-1"
                onClick={() => setTable((t) => !t)} aria-expanded={table}>
                {table ? 'Masquer' : 'Voir'} le tableau
            </button>

            {table && (
                <div className="mt-2 overflow-x-auto">
                    <table className="table table-sm">
                        <thead>
                            {/* Des en-têtes de GOLFEUR, pas de statisticien.
                                « Écart » et « Marge » ne disaient à personne de
                                quoi il s'agissait : le premier est un écart à
                                QUOI, la seconde une marge de quoi. Ce qu'on
                                veut savoir tient en deux questions — de
                                combien, et est-ce que ça compte. */}
                            <tr>
                                <th>Jour</th>
                                <th className="text-right">Balles</th>
                                {/* Le MÊME mot que sur le graphique — « l'ordinaire »
                                    y est écrit sur la ligne de zéro. Un tableau qui
                                    rebaptise ce que la courbe vient de nommer
                                    oblige à refaire le lien tout seul. */}
                                <th className="whitespace-normal text-right">
                                    Écart à l’ordinaire
                                </th>
                                <th className="text-right">Ça compte&nbsp;?</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[...points].reverse().map((p) => (
                                <tr key={p.day}>
                                    <td>{shortDate(p.day)}</td>
                                    <td className="text-right tabular-nums opacity-70">{p.balls}</td>
                                    <td className="text-right tabular-nums font-semibold">
                                        {signe(p.ecart)} pt
                                    </td>
                                    <td className="text-right">
                                        {sorti(p)
                                            ? <span className="font-semibold">oui</span>
                                            : <span className="opacity-60">non</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <p className="mt-1 px-0.5 text-xs opacity-50">
                        « Oui » quand l’écart dépasse ce que le hasard des balles peut
                        produire à lui seul. Sinon la journée ne prouve rien, en bien
                        comme en mal.
                    </p>
                </div>
            )}
        </div>
    );
}
